import React, { useState } from "react";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { db } from "../firebase";
import { ref, get, remove } from "firebase/database";
import { getStorage, ref as storageRef, getDownloadURL, deleteObject } from "firebase/storage";

// Step 1: Sign In
function SignInStep({ onSignIn, loading, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  return (
    <form onSubmit={e => { e.preventDefault(); onSignIn(email, password); }} className="bg-gray-50 p-8 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">Sign In</h2>
      <input
        type="email"
        placeholder="Email"
        className="mb-2 w-full p-2 border rounded"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
      />
      <input
        type="password"
        placeholder="Password"
        className="mb-4 w-full p-2 border rounded"
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
      />
      <button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        disabled={loading}
      >
        {loading ? "Signing in..." : "Sign In"}
      </button>
      {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
    </form>
  );
}

// Step 2: Delete All User Data
function DeleteUserDataStep({ user, onDelete, loading, error, deleted }) {
  return (
    <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
      <h2 className="text-xl font-bold mb-4">Delete All User Data</h2>
      <p className="mb-4">
        This will delete all your data under <span className="font-mono">/Users/{user.uid}</span> including vehicles, chargers, profile info,
        <span className="block mt-2 text-orange-600 font-semibold">and any balance in your wallet will also be permanently deleted.</span>
      </p>
      <button
        onClick={onDelete}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
        disabled={loading || deleted}
      >
        Delete All User Data
      </button>
      {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
      {deleted && <p className="text-green-600 mt-4 text-center">All user data deleted.</p>}
    </div>
  );
}

// Step 3: Delete Profile Photo
function DeletePhotoStep({ user, photoUrl, onDelete, loading, error, deleted }) {
  return (
    <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
      <h2 className="text-xl font-bold mb-4">Delete Profile Photo</h2>
      {photoUrl ? (
        <img src={photoUrl} alt="Profile" className="mx-auto rounded-full w-24 h-24 object-cover mb-2" />
      ) : (
        <span className="text-gray-500">No photo</span>
      )}
      <button
        onClick={onDelete}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mt-4"
        disabled={loading || deleted}
      >
        Delete Photo
      </button>
      {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
      {deleted && <p className="text-green-600 mt-4 text-center">Photo deleted.</p>}
    </div>
  );
}

// Step 3: Delete Ticket Data
function DeleteTicketDataStep({ user, onDelete, loading, error, deleted }) {
  return (
    <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
      <h2 className="text-xl font-bold mb-4">Delete Ticket Data</h2>
      <p className="mb-4">
        This will delete all your ticket data under <span className="font-mono">/tickets/{user.uid}</span>.
      </p>
      <button
        onClick={onDelete}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
        disabled={loading || deleted}
      >
        Delete Ticket Data
      </button>
      {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
      {deleted && <p className="text-green-600 mt-4 text-center">All ticket data deleted.</p>}
    </div>
  );
}

// Step 4: Delete Account
function DeleteAccountStep({ user, onDelete, loading, error }) {
  return (
    <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
      <h2 className="text-xl font-bold mb-4">Delete Account</h2>
      <p className="mb-4">Signed in as: <span className="font-semibold">{user.email}</span></p>
      <button
        onClick={onDelete}
        className="w-full bg-gray-800 hover:bg-black text-white font-bold py-2 px-4 rounded"
        disabled={loading}
      >
        Delete Account
      </button>
      {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
    </div>
  );
}

// Main Stepper
const DeleteDataPage = () => {
  const [step, setStep] = useState(0);
  const [user, setUser] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userDataDeleted, setUserDataDeleted] = useState(false);
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [ticketDeleted, setTicketDeleted] = useState(false);

  // Update steps array
  const steps = [
    "Delete All User Data",
    "Delete Profile Photo",
    "Delete Ticket Data",
    "Delete Account"
  ];

  // Step 1: Sign In
  const handleSignIn = async (email, password) => {
    setLoading(true);
    setError("");
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(userCredential.user);

      // Fetch profile photo
      const storage = getStorage();
      const photoRef = storageRef(storage, `profile-photos/${userCredential.user.uid}`);
      try {
        const url = await getDownloadURL(photoRef);
        setPhotoUrl(url);
      } catch {
        setPhotoUrl(null);
      }

      setStep(1);
    } catch (err) {
      setError("Sign-in failed or data fetch error: invalid email or password");
    }
    setLoading(false);
  };

  // Step 2: Delete All User Data
  const handleDeleteUserData = async () => {
    setLoading(true);
    setError("");
    try {
      await remove(ref(db, `/Users/${user.uid}`));
      setUserDataDeleted(true);
      setStep(2);
    } catch (err) {
      // Log detailed error for debugging (not exposed to user)
      console.error("Error deleting user data:", err);
      setError("Failed to delete user data. Please try again.");
    }
    setLoading(false);
  };

  // Step 3: Delete Photo
  const handleDeletePhoto = async () => {
    setLoading(true);
    setError("");
    try {
      const storage = getStorage();
      const photoRef = storageRef(storage, `profile-photos/${user.uid}`);
      await deleteObject(photoRef);
      setPhotoUrl(null);
      setPhotoDeleted(true);
      setStep(3);
    } catch (err) {
      setPhotoUrl(null);
      setPhotoDeleted(true); // Even if not found, treat as deleted
      setStep(3);
    }
    setLoading(false);
  };

  // Step 3: Delete Ticket Data
  const handleDeleteTicketData = async () => {
    setLoading(true);
    setError("");
    try {
      await remove(ref(db, `/tickets/${user.uid}`));
      setTicketDeleted(true);
      setStep(4);
    } catch (err) {
      // Log detailed error for debugging (not exposed to user)
      console.error("Error deleting ticket data:", err);
      setError("Failed to delete ticket data. Please try again.");
    }
    setLoading(false);
  };

  // Step 4: Delete Account
  const handleDeleteAccount = async () => {
    setLoading(true);
    setError("");
    try {
      await user.delete();
      setUser(null);
      setStep(5);
    } catch (err) {
      // Log detailed error for debugging (not exposed to user)
      console.error("Error deleting account:", err);
      if (err.code === "auth/requires-recent-login") {
        setError("Please sign in again to delete your account.");
      } else {
        setError("Failed to delete account. Please try again.");
      }
    }
    setLoading(false);
  };

  return (
    <div className="py-20 bg-white">
      <div className="max-w-md mx-auto px-4">
        <h1 className="text-3xl font-bold text-center mb-8">Delete Your Data</h1>
        {step === 0 ? (
          <SignInStep onSignIn={handleSignIn} loading={loading} error={error} />
        ) : (
          <>
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-700">
                  Step {step} of {steps.length}
                </span>
                <span className="text-sm text-gray-500">{steps[step - 1] || "Completed"}</span>
              </div>
              <div className="flex space-x-2">
                {steps.map((label, idx) => (
                  <div
                    key={label}
                    className={`flex-1 h-2 rounded ${idx < step ? "bg-blue-600" : "bg-gray-300"}`}
                    title={label}
                  />
                ))}
              </div>
            </div>
            {step === 1 && (
              <DeleteUserDataStep
                user={user}
                onDelete={handleDeleteUserData}
                loading={loading}
                error={error}
                deleted={userDataDeleted}
              />
            )}
            {step === 2 && (
              <DeletePhotoStep
                user={user}
                photoUrl={photoUrl}
                onDelete={handleDeletePhoto}
                loading={loading}
                error={error}
                deleted={photoDeleted}
              />
            )}
            {step === 3 && (
              <DeleteTicketDataStep
                user={user}
                onDelete={handleDeleteTicketData}
                loading={loading}
                error={error}
                deleted={ticketDeleted}
              />
            )}
            {step === 4 && (
              <DeleteAccountStep
                user={user}
                onDelete={handleDeleteAccount}
                loading={loading}
                error={error}
              />
            )}
            {step === 5 && (
              <div className="bg-gray-50 p-8 rounded-lg shadow-md text-center">
                <h2 className="text-xl font-bold mb-4">Account Deleted</h2>
                <p className="text-green-600">Your account and all related data have been deleted.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteDataPage;